import React, { useState } from "react";
import "@smile_identity/smart-camera-web";
import SmartCameraComponent from "./SmartCamera";
import { SlArrowLeft } from "react-icons/sl";
import { Link, useNavigate } from "react-router-dom";
import { PiSealWarningFill } from "react-icons/pi";
import { validateBvn } from "../utils/validations";
import useAuthAxios from "../utils/baseAxios";
import { PRIVACY_URL } from "../constants/links";
import { API } from "../constants/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { errorMessage } from "../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import { useUser } from "../context/user-context";

const Kyc = () => {
  const [partnerParams, setPartnerParams] = useState<string>("");
  const [images, setImages] = useState<
    { image_type_id: number; image: string }[]
  >([]);

  const [bvn, setBvn] = useState<string>("");
  const { refetch } = useUser();
  const [surname, setSurname] = useState<string>("");
  const [isImageCaptured, setIsImageCaptured] = useState<boolean>(false);
  const [submit, setSubmit] = useState(0);
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();
  const navigate = useNavigate();
  const handlePostKyc = async ({ bvn, partner_params, images }: any) => {
    const response = await axiosInstance.post(API.verifyKyc, {
      bvn,
      partner_params,
      images,
    });
    return response.data;
  };
  const completeKyc = useMutation({
    mutationFn: handlePostKyc,
    onSuccess: (r) => {
      toast.success(r.message);
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      queryClient.invalidateQueries({ queryKey: ["get-kyc-details"] });
      refetch();
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    },
    onError: (error: any) => {
      toast.error(
        errorMessage((error?.data as any)?.message || String(error?.data))
      );
    },
  });

  const handleVerify = () => {
    if (!validateBvn(bvn)) {
      return;
    }
    setIsImageCaptured(true);
  };
  // console.log(bvn, partnerParams, images);
  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start pt-10 pb-20 overflow-auto bg-white dark:bg-[#141414]  bg-opacity-10 backdrop-blur-sm">
      <div className=" w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 ">
        <Link
          to="/kyc-onboarding"
          className="flex w-full mt-4 just items-center gap-2 "
        >
          <SlArrowLeft className="dark:text-[#D8D8D8] text-gray-800 text-[12px]" />
          <h4 className="dark:text-[#D8D8D8] text-gray-800 text-[14px]">
            Back
          </h4>
        </Link>
        <div className="w-full px-4 mt-6 flex flex-col items-center justify-center">
          <h4 className="text-[24px] dark:text-gray-100 text-paragraph font-sora font-semibold">
            KYC Identification
          </h4>
          <h6 className="text-[#869AA9] dark:text-gray-400 lgss:text-[14px] mt-1">
            Your verification allows us to confirm your BVN.
          </h6>
          <div className=" mt-6 py-6 w-full flex flex-col items-center justify-center ">
            <div className="w-full">
              <label className="text-gray-800 text-[14px]  dark:text-white">
                BVN Number
              </label>
              <input
                type="number"
                value={bvn}
                onChange={(e) => setBvn(e.target.value)}
                placeholder="Enter your BVN"
                className="w-full dark:text-gray-400 text-gray-800  dark:border-gray-400 bg-[#FAFAFA] dark:bg-transparent h-[52px] mt-2   outline-none text-[14px] border border-gray-300 bg-transparent px-4 spin-button-none rounded-xl "
              />
              <h4 className="text-gray-800 dark:text-gray-300  text-[11px] mt-2">
                Dial *565*0# to check for your BVN
              </h4>
            </div>
            <div className="w-full mt-6">
              <label className="text-gray-800 text-[14px] dark:text-white">
                Surname
              </label>
              <div className="w-full flex justify-between px-4  items-center dark:text-gray-400 text-gray-800  dark:border-gray-400 bg-[#FAFAFA] dark:bg-transparent h-[52px] mt-2 text-[14px] border border-gray-300 bg-transparent  spin-button-none rounded-xl">
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="eg: Chidi"
                  className="w-10/12   outline-none bg-transparent "
                />
              </div>
            </div>
            <div className="w-full bg-[#E8F3FC] dark:bg-secondary_button gap-6 rounded-xl text-[13px] text-[#4B5563] dark:text-gray-100 p-4 mt-6 flex justify-start items-start">
              <div>
                <PiSealWarningFill className="text-[20px]" />
              </div>
              <ul className="list-disc ">
                <li>Your information is safe and we respect your privacy.</li>
                <li>
                  We are not going to be sharing your data with anyone. Read our{" "}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className=" font-bold  font-sora  underline"
                  >
                    Privacy policy
                  </a>
                </li>
              </ul>
            </div>
            {isImageCaptured && (
              <SmartCameraComponent
                setIsImageCaptured={setIsImageCaptured}
                setSubmit={setSubmit}
                setPartnerParams={setPartnerParams}
                setImages={setImages}
              />
            )}
            <button
              onClick={() => {
                if (submit === 0) {
                  handleVerify();
                } else if (submit === 1) {
                  completeKyc.mutate({
                    bvn: bvn,
                    partner_params: {
                      libraryVersion: partnerParams,
                    },
                    images: images,
                  });
                }
              }}
              disabled={!bvn}
              className={`w-full h-[52px] rounded-[18px] mt-8 ${
                !bvn
                  ? "dark:text-gray-400 dark:bg-gray-600 bg-gray-400 text-gray-100"
                  : "bg-secondary_button text-white"
              }  flex justify-center items-center  font-semibold`}
            >
              {submit === 0 ? (
                " Next"
              ) : submit === 1 && completeKyc.isPending ? (
                <ClipLoader color="#FFFFFF" size={30} />
              ) : (
                "Submit"
              )}
            </button>
            <h4 className="text-[14px] mt-4 text-paragraph dark:text-gray-400 font-semibold">
              I will do this later{" "}
              <Link to="/dashboard" className="text-secondary_button">
                Skip
              </Link>
            </h4>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kyc;
